/**
 * App router: route definitions and history mode.
 *
 * history: createWebHistory(import.meta.env.BASE_URL) – uses the browser's HTML5 History API
 * so the address bar updates when you navigate (e.g. /api/v0/payments). The BASE_URL is the
 * app's base path (from Vite: import.meta.env.BASE_URL, usually '/'). All route paths are
 * resolved relative to this base, so the full URL is origin + base + path.
 */
import { createRouter, createWebHistory } from 'vue-router'
import { routeBase } from '@/config/router'
import { ErrorCode } from '@/errors/codes'
import { errorPathForCode } from '@/errors/code_to_error_route'
import NotFound404 from '@/components/errors/NotFound404.vue'
import UnauthorizedError401 from '@/components/errors/UnauthorizedError401.vue'
import WelcomeTab from '@/components/main/tabs/WelcomeTab.vue'
import ClientsTab from '@/components/main/tabs/ClientsTab.vue'
import PaymentsTab from '@/components/main/tabs/PaymentsTab.vue'
import ServicesTab from '@/components/main/tabs/ServicesTab.vue'
import RulesTab from '@/components/main/tabs/RulesTab.vue'
import DiseasesTab from '@/components/main/tabs/DiseasesTab.vue'
import FinancesTab from '@/components/main/tabs/FinancesTab.vue'
import StuffTab from '@/components/main/tabs/StuffTab.vue'
import BookingTab from '@/components/main/tabs/BookingTab.vue'
import ClientsStatementPage from '@/components/public/ClientsStatementPage.vue'
import KeycloakUnavailableError503 from '@/components/errors/KeycloakUnavailableError503.vue'
import NoFragmentInAuthResponseError401 from '@/components/errors/NoFragmentInAuthResponseError401.vue'
import NoCodeInAuthResponseError401 from '@/components/errors/NoCodeInAuthResponseError401.vue'
import NoStateInAuthResponseError401 from '@/components/errors/NoStateInAuthResponseError401.vue'
import InvalidStateInAuthResponseError401 from '@/components/errors/InvalidStateInAuthResponseError401.vue'
import NoSessionStateInAuthResponseError401 from '@/components/errors/NoSessionStateInAuthResponseError401.vue'
import NoIssInAuthResponseError401 from '@/components/errors/NoIssInAuthResponseError401.vue'
import InvalidIssInAuthResponseError401 from '@/components/errors/InvalidIssInAuthResponseError401.vue'
import NoNonceInStorageError401 from '@/components/errors/NoNonceInStorageError401.vue'
import AuthCodeTokenExchangeFailed401 from '@/components/errors/AuthCodeTokenExchangeFailed401.vue'
import BackendBffError401 from '@/components/errors/BackendBffError401.vue'
import ClientsByIdsNoClientsFound403 from '@/components/errors/ClientsByIdsNoClientsFound403.vue'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    { path: '/', redirect: `${routeBase}/welcome` },
    { path: routeBase, redirect: `${routeBase}/welcome` },
    { path: `${routeBase}/welcome`, name: 'Welcome', component: WelcomeTab },
    { path: `${routeBase}/clients`, name: 'Clients', component: ClientsTab },
    { path: `${routeBase}/payments`, name: 'Payments', component: PaymentsTab },
    // Categories and types now live in the Services tab's panes; keep old links working.
    { path: `${routeBase}/service-categories`, redirect: `${routeBase}/services` },
    { path: `${routeBase}/service-types`, redirect: `${routeBase}/services` },
    { path: `${routeBase}/services`, name: 'Services', component: ServicesTab },
    { path: `${routeBase}/rules`, name: 'Rules', component: RulesTab },
    { path: `${routeBase}/diseases`, name: 'Diseases', component: DiseasesTab },
    { path: `${routeBase}/finances`, name: 'Finances', component: FinancesTab },
    { path: `${routeBase}/stuff`, name: 'Stuff', component: StuffTab },
    { path: `${routeBase}/booking`, name: 'Booking', component: BookingTab },
    // Public, standalone: no login, no app shell (see App.vue's `isStandalone`
    // check) — a single special URL for sharing the temporary clients
    // statement report outside the authenticated app.
    {
      path: '/statement',
      name: 'PublicClientsStatement',
      component: ClientsStatementPage,
      meta: { standalone: true },
    },
    // Dedicated error pages at fixed URLs.
    {
      path: errorPathForCode(ErrorCode.Unauthorized),
      name: 'Unauthorized',
      component: UnauthorizedError401,
    },
    {
      path: errorPathForCode(ErrorCode.AuthCodeTokenExchangeFailed),
      name: 'AuthCodeTokenExchangeFailed',
      component: AuthCodeTokenExchangeFailed401,
    },
    {
      path: errorPathForCode(ErrorCode.KeycloakTokenSignatureInvalid),
      name: 'KeycloakTokenSignatureInvalid',
      component: BackendBffError401,
      props: () => ({ code: ErrorCode.KeycloakTokenSignatureInvalid }),
    },
    {
      path: errorPathForCode(ErrorCode.KeycloakTokenExpirationInvalid),
      name: 'KeycloakTokenExpirationInvalid',
      component: BackendBffError401,
      props: () => ({ code: ErrorCode.KeycloakTokenExpirationInvalid }),
    },
    {
      path: errorPathForCode(ErrorCode.KeycloakJwtDecodeFailed),
      name: 'KeycloakJwtDecodeFailed',
      component: BackendBffError401,
      props: () => ({ code: ErrorCode.KeycloakJwtDecodeFailed }),
    },
    {
      path: errorPathForCode(ErrorCode.UnknownErrorDuringJwtDecoding),
      name: 'UnknownErrorDuringJwtDecoding',
      component: BackendBffError401,
      props: () => ({ code: ErrorCode.UnknownErrorDuringJwtDecoding }),
    },
    {
      path: errorPathForCode(ErrorCode.KeycloakIdTokenNonceViolation),
      name: 'KeycloakIdTokenNonceViolation',
      component: BackendBffError401,
      props: () => ({ code: ErrorCode.KeycloakIdTokenNonceViolation }),
    },
    {
      path: errorPathForCode(ErrorCode.KeycloakIntrospectionFailed),
      name: 'KeycloakIntrospectionFailed',
      component: BackendBffError401,
      props: () => ({ code: ErrorCode.KeycloakIntrospectionFailed }),
    },
    {
      path: errorPathForCode(ErrorCode.XauthDataCookieTooLarge),
      name: 'XauthDataCookieTooLarge',
      component: BackendBffError401,
      props: () => ({ code: ErrorCode.XauthDataCookieTooLarge }),
    },
    {
      path: errorPathForCode(ErrorCode.KeycloakUserinfoTimeout),
      name: 'KeycloakUserinfoTimeout',
      component: BackendBffError401,
      props: () => ({ code: ErrorCode.KeycloakUserinfoTimeout }),
    },
    {
      path: errorPathForCode(ErrorCode.KeycloakUserinfoHttpError),
      name: 'KeycloakUserinfoHttpError',
      component: BackendBffError401,
      props: () => ({ code: ErrorCode.KeycloakUserinfoHttpError }),
    },
    {
      path: errorPathForCode(ErrorCode.KeycloakUserinfoUnknownError),
      name: 'KeycloakUserinfoUnknownError',
      component: BackendBffError401,
      props: () => ({ code: ErrorCode.KeycloakUserinfoUnknownError }),
    },
    {
      path: errorPathForCode(ErrorCode.UserinfoMissingContact),
      name: 'UserinfoMissingContact',
      component: BackendBffError401,
      props: () => ({ code: ErrorCode.UserinfoMissingContact }),
    },
    {
      path: errorPathForCode(ErrorCode.UserinfoMissingName),
      name: 'UserinfoMissingName',
      component: BackendBffError401,
      props: () => ({ code: ErrorCode.UserinfoMissingName }),
    },
    {
      path: errorPathForCode(ErrorCode.UserinfoMissingGender),
      name: 'UserinfoMissingGender',
      component: BackendBffError401,
      props: () => ({ code: ErrorCode.UserinfoMissingGender }),
    },
    {
      path: errorPathForCode(ErrorCode.UserinfoInvalidGender),
      name: 'UserinfoInvalidGender',
      component: BackendBffError401,
      props: () => ({ code: ErrorCode.UserinfoInvalidGender }),
    },
    {
      path: errorPathForCode(ErrorCode.UserinfoMissingAddress),
      name: 'UserinfoMissingAddress',
      component: BackendBffError401,
      props: () => ({ code: ErrorCode.UserinfoMissingAddress }),
    },
    {
      path: errorPathForCode(ErrorCode.UserinfoValidation),
      name: 'UserinfoValidation',
      component: BackendBffError401,
      props: () => ({ code: ErrorCode.UserinfoValidation }),
    },
    {
      path: errorPathForCode(ErrorCode.UserinfoUnknownGroup),
      name: 'UserinfoUnknownGroup',
      component: BackendBffError401,
      props: () => ({ code: ErrorCode.UserinfoUnknownGroup }),
    },
    {
      path: errorPathForCode(ErrorCode.UserinfoUnknownRole),
      name: 'UserinfoUnknownRole',
      component: BackendBffError401,
      props: () => ({ code: ErrorCode.UserinfoUnknownRole }),
    },
    {
      path: errorPathForCode(ErrorCode.XsrfTokenCookieMissing),
      name: 'XsrfTokenCookieMissing',
      component: BackendBffError401,
      props: () => ({ code: ErrorCode.XsrfTokenCookieMissing }),
    },
    {
      path: errorPathForCode(ErrorCode.NoCsrfHeader),
      name: 'NoCsrfHeader',
      component: BackendBffError401,
      props: () => ({ code: ErrorCode.NoCsrfHeader }),
    },
    {
      path: errorPathForCode(ErrorCode.CsrfTokenMismatch),
      name: 'CsrfTokenMismatch',
      component: BackendBffError401,
      props: () => ({ code: ErrorCode.CsrfTokenMismatch }),
    },
    {
      path: errorPathForCode(ErrorCode.XauthDataCookieDecryptFailed),
      name: 'XauthDataCookieDecryptFailed',
      component: BackendBffError401,
      props: () => ({ code: ErrorCode.XauthDataCookieDecryptFailed }),
    },
    {
      path: errorPathForCode(ErrorCode.XauthDataCookieDecompressFailed),
      name: 'XauthDataCookieDecompressFailed',
      component: BackendBffError401,
      props: () => ({ code: ErrorCode.XauthDataCookieDecompressFailed }),
    },
    {
      path: errorPathForCode(ErrorCode.XauthDataCookieDeserializeFailed),
      name: 'XauthDataCookieDeserializeFailed',
      component: BackendBffError401,
      props: () => ({ code: ErrorCode.XauthDataCookieDeserializeFailed }),
    },
    {
      path: errorPathForCode(ErrorCode.XauthDataCookieUnknownError),
      name: 'XauthDataCookieUnknownError',
      component: BackendBffError401,
      props: () => ({ code: ErrorCode.XauthDataCookieUnknownError }),
    },
    {
      path: errorPathForCode(ErrorCode.AccessTokenMissing),
      name: 'AccessTokenMissing',
      component: BackendBffError401,
      props: () => ({ code: ErrorCode.AccessTokenMissing }),
    },
    {
      path: errorPathForCode(ErrorCode.SessionNotAuthenticated),
      name: 'SessionNotAuthenticated',
      component: BackendBffError401,
      props: () => ({ code: ErrorCode.SessionNotAuthenticated }),
    },
    {
      path: errorPathForCode(ErrorCode.AccessTokenRefreshFailed),
      name: 'AccessTokenRefreshFailed',
      component: BackendBffError401,
      props: () => ({ code: ErrorCode.AccessTokenRefreshFailed }),
    },
    {
      path: errorPathForCode(ErrorCode.PermissionDenied),
      name: 'PermissionDenied',
      component: BackendBffError401,
      props: () => ({ code: ErrorCode.PermissionDenied }),
    },
    {
      path: errorPathForCode(ErrorCode.ClientsByIdsNoClientsFound),
      name: 'ClientsByIdsNoClientsFound',
      component: ClientsByIdsNoClientsFound403,
    },
    {
      path: errorPathForCode(ErrorCode.NoFragmentInAuthResponse),
      name: 'NoFragmentInAuthResponse',
      component: NoFragmentInAuthResponseError401,
    },
    {
      path: errorPathForCode(ErrorCode.NoCodeInAuthResponse),
      name: 'NoCodeInAuthResponse',
      component: NoCodeInAuthResponseError401,
    },
    {
      path: errorPathForCode(ErrorCode.NoStateInAuthResponse),
      name: 'NoStateInAuthResponse',
      component: NoStateInAuthResponseError401,
    },
    {
      path: errorPathForCode(ErrorCode.InvalidStateInAuthResponse),
      name: 'InvalidStateInAuthResponse',
      component: InvalidStateInAuthResponseError401,
    },
    {
      path: errorPathForCode(ErrorCode.NoSessionStateInAuthResponse),
      name: 'NoSessionStateInAuthResponse',
      component: NoSessionStateInAuthResponseError401,
    },
    {
      path: errorPathForCode(ErrorCode.NoIssInAuthResponse),
      name: 'NoIssInAuthResponse',
      component: NoIssInAuthResponseError401,
    },
    {
      path: errorPathForCode(ErrorCode.InvalidIssInAuthResponse),
      name: 'InvalidIssInAuthResponse',
      component: InvalidIssInAuthResponseError401,
    },
    {
      path: errorPathForCode(ErrorCode.NoNonceInStorage),
      name: 'NoNonceInStorage',
      component: NoNonceInStorageError401,
    },
    { path: errorPathForCode(ErrorCode.NotFound), name: 'NotFound', component: NotFound404 },
    {
      path: errorPathForCode(ErrorCode.KeycloakUnavailable),
      name: 'KeycloakUnavailable',
      component: KeycloakUnavailableError503,
    },
    // Catch-all: redirect to the dedicated 404 URL, passing the original path as a query param.
    {
      path: '/:pathMatch(.*)*',
      redirect: (to) => ({
        path: errorPathForCode(ErrorCode.NotFound),
        query: { from: to.fullPath },
      }),
    },
  ],
})

export default router
